import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import BrandMark from "./BrandMark";
import { ShareButtons } from "./ShareButtons";
import { SearchBox } from "./SearchBox";
import InstallPWA from "./InstallPWA";

const NAV = [
    { to: "/", label: "Início" },
    { to: "/produtos", label: "Produtos" },
    { to: "/categorias", label: "Categorias" },
];

const Logo = ({ inverted = false }) => (
    <Link to="/" data-testid="logo-link" className="flex items-center gap-2.5 group">
        <BrandMark
            size={40}
            bgClass={inverted ? "bg-brand-gold/15 border border-brand-gold/40" : "bg-brand-ink"}
            colorClass="text-brand-gold"
            className="transition-transform group-hover:rotate-3"
        />
        <div className="leading-tight">
            <div className={`font-heading font-bold text-[13px] tracking-wider uppercase ${inverted ? "text-white" : "text-brand-ink"}`}>
                Vitrine
            </div>
            <div className={`font-heading text-[11px] tracking-[0.25em] uppercase text-brand-gold -mt-0.5`}>
                Deus Proverá
            </div>
        </div>
    </Link>
);

const Header = () => {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const loc = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 8);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => { setOpen(false); }, [loc.pathname]);

    return (
        <header
            data-testid="site-header"
            className={`sticky top-0 z-50 backdrop-blur-xl transition-all ${
                scrolled ? "bg-white/95 shadow-[0_1px_0_0_rgba(0,0,0,0.04)]" : "bg-white/85"
            } border-b border-brand-border`}
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
                <Logo />
                <nav className="hidden md:flex items-center gap-10">
                    {NAV.map((n) => (
                        <NavLink
                            key={n.to}
                            to={n.to}
                            end={n.to === "/"}
                            data-testid={`nav-${n.label.toLowerCase()}`}
                            className={({ isActive }) =>
                                `text-sm font-medium transition-colors relative ${
                                    isActive ? "text-brand-ink" : "text-brand-inkSoft hover:text-brand-ink"
                                }`
                            }
                        >
                            {({ isActive }) => (
                                <span className="relative inline-block py-1">
                                    {n.label}
                                    {isActive && (
                                        <span className="absolute -bottom-1 left-0 right-0 h-[2px] bg-brand-gold rounded-full" />
                                    )}
                                </span>
                            )}
                        </NavLink>
                    ))}
                </nav>
                <div className="flex items-center gap-3 flex-1 md:flex-initial md:min-w-[280px] justify-end">
                    <SearchBox className="hidden md:block flex-1 md:max-w-sm" />
                    <button
                        onClick={() => setOpen((v) => !v)}
                        data-testid="mobile-menu-toggle"
                        className="md:hidden inline-flex items-center justify-center w-10 h-10 rounded-sm border border-brand-border hover:border-brand-ink transition-colors"
                        aria-label="Menu"
                    >
                        {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                    </button>
                </div>
            </div>
            {open && (
                <div data-testid="mobile-menu" className="md:hidden border-t border-brand-border bg-white">
                    <div className="px-4 py-3 space-y-2">
                        <SearchBox onNavigate={() => setOpen(false)} />
                        {NAV.map((n) => (
                            <NavLink
                                key={n.to}
                                to={n.to}
                                end={n.to === "/"}
                                data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                                className={({ isActive }) =>
                                    `block px-3 py-3 text-base font-medium rounded-sm ${
                                        isActive ? "bg-brand-bg text-brand-ink" : "text-brand-inkSoft hover:bg-brand-bg"
                                    }`
                                }
                            >
                                {n.label}
                            </NavLink>
                        ))}
                    </div>
                </div>
            )}
        </header>
    );
};

const Footer = () => (
    <footer data-testid="site-footer" className="bg-brand-ink text-brand-bg mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-10">
                <div className="md:col-span-2">
                    <Logo inverted />
                    <p className="mt-6 text-sm text-white/60 max-w-md leading-relaxed">
                        Estilo, qualidade e propósito. Produtos selecionados da marca <strong className="text-white font-semibold">Vitrine Deus Proverá</strong>, com compra segura nas plataformas oficiais (Mercado Livre, Shopee, Amazon e outras).
                    </p>
                    {/* Compartilhar a loja */}
                    <div className="mt-6" data-testid="footer-share-block">
                        <div className="text-[11px] uppercase tracking-[0.3em] font-bold text-brand-gold mb-3">Indique a loja</div>
                        <ShareButtons
                            url={typeof window !== "undefined" ? window.location.origin : ""}
                            title="Conheça a Vitrine Deus Proverá — estilo, qualidade e propósito:"
                            testIdPrefix="footer-share"
                            variant="dark"
                            className="max-w-md"
                        />
                    </div>
                </div>
                <div>
                    <h4 className="font-heading font-semibold text-sm uppercase tracking-[0.2em] text-brand-gold mb-4">Loja</h4>
                    <ul className="space-y-2 text-sm text-white/70">
                        <li><Link data-testid="footer-link-produtos" to="/produtos" className="hover:text-white transition">Todos os produtos</Link></li>
                        <li><Link data-testid="footer-link-categorias" to="/categorias" className="hover:text-white transition">Categorias</Link></li>
                        <li><Link data-testid="footer-link-novidades" to="/produtos?filter=novidades" className="hover:text-white transition">Novidades</Link></li>
                        <li><Link data-testid="footer-link-mais-vendidos" to="/produtos?filter=mais-vendidos" className="hover:text-white transition">Mais vendidos</Link></li>
                    </ul>
                </div>
                <div>
                    <h4 className="font-heading font-semibold text-sm uppercase tracking-[0.2em] text-brand-gold mb-4">Contato</h4>
                    <ul className="space-y-2 text-sm text-white/70">
                        <li>Compras: pelas plataformas</li>
                        <li>Entrega: pela plataforma escolhida</li>
                        <li>Suporte: pela plataforma de compra</li>
                    </ul>
                </div>
            </div>
            <div className="mt-12 pt-8 border-t border-white/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                <p className="text-xs text-white/40">
                    © {new Date().getFullYear()} Vitrine Deus Proverá. Todos os direitos reservados.
                </p>
                <Link
                    to="/admin/login"
                    data-testid="footer-admin-link"
                    className="text-[11px] uppercase tracking-[0.3em] text-white/30 hover:text-brand-gold transition-colors underline-offset-4 hover:underline"
                >
                    Administrador
                </Link>
            </div>
        </div>
    </footer>
);

const Layout = ({ children }) => (
    <div data-testid="layout-root" className="min-h-screen flex flex-col bg-brand-bg">
        <Header />
        <main className="flex-1">{children}</main>
        <Footer />
        <InstallPWA />
    </div>
);

export default Layout;
